import store from '@/store';
import doRequest from '@/api/dssRequest';
import { translate } from '@/translation/native_translate';
import utility from '@/shared/utility';

/**
 * Error handler for the function `addProductToReservationError`
 * @param {object} error Error object received from BE
 */
function _onAddProductToReservationError(error) {
  let errorMsg = translate('FAILED_ADD_SHOPPINGLIST');
  let showErrorDialog = true;

  switch (error.data.ErrorCode) {
    case 'E_PO_006':
      errorMsg = translate('E_PO_006_PRODUCT');
      break;
    case 'E_PO_023':
      errorMsg = translate('E_PO_023_ADDPRODUCT');
      break;
    case 'E_PO_024':
      errorMsg = translate('E_PO_024_ADDPRODUCT');
      break;
    case 'E_DEL_007': {
      // Product excluded by delivery / pickup event
      const currentList = store.getters.getCurrentSelectedShoppingList;
      if (currentList.Temporary) {
        showErrorDialog = false;
      } else if (currentList.SubEventType != null) {
        showErrorDialog = true;

        if (
          currentList.SubEventType === 'Delivery' ||
          currentList.SubEventType === 'Shiplee'
        ) {
          errorMsg = translate('E_DEL_007_DELIVERY_ADD_PRODUCT');
        } else {
          errorMsg = translate('E_DEL_007_PICKUP_ADD_PRODUCT');
        }
      }

      break;
    }
    case 'E_SL_001':
    case 'E_SL_002':
      errorMsg = translate('E_SL_001');
      break;
    case 'E_USR_077':
      errorMsg = translate('E_USR_077_ADD_TO_LIST');
      break;
    case 'E_PCAP_001':
      errorMsg = utility.sprintf(
        translate('E_PCAP_001_ADD_PROD'),
        translate('SHOPPING_LIST_LOWERCASE')
      );
      break;
    case 'E_PCAP_002':
      errorMsg = utility.sprintf(
        translate('E_PCAP_002_ADD_PROD'),
        translate('SHOPPING_LIST_LOWERCASE')
      );
      break;
    default:
      showErrorDialog = false;
      break;
  }

  if (showErrorDialog) {
    window.EventBus.fire('activateErrorDialog', errorMsg, false);
  } else {
    window.EventBus.fire('activateSnackBar', {
      text: errorMsg,
      color: 'error',
    });
  }
}

/**
 * Error handler for the function `changeProductOnReservationError`
 * @param {object} error Error object received from BE
 */
function _onChangeProductOnReservationError(error) {
  let errorMsg = '';
  switch (error.data.ErrorCode) {
    case 'E_PO_006':
      errorMsg = translate('E_PO_006_PRODUCT');
      window.EventBus.fire('activateErrorDialog', errorMsg, false);
      break;
    case 'E_PO_023':
      errorMsg = translate('E_PO_023_CHANGEPRODUCT');
      window.EventBus.fire('activateErrorDialog', errorMsg, false);
      break;
    case 'E_PO_024':
      errorMsg = translate('E_PO_024_CHANGEPRODUCT');
      window.EventBus.fire('activateErrorDialog', errorMsg, false);
      break;
    case 'E_PCAP_001':
      errorMsg = utility.sprintf(
        translate('E_PCAP_001_CHANGE_PROD'),
        translate('SHOPPING_LIST_LOWERCASE')
      );
      window.EventBus.fire('activateErrorDialog', errorMsg, false);
      break;
    case 'E_PCAP_002':
      errorMsg = utility.sprintf(
        translate('E_PCAP_002_CHANGE_PROD'),
        translate('SHOPPING_LIST_LOWERCASE')
      );
      window.EventBus.fire('activateErrorDialog', errorMsg, false);
      break;
    default:
      window.EventBus.fire('activateSnackBar', {
        text: translate('FAILED_ADD_SHOPPINGLIST'),
        color: 'error',
      });
      break;
  }
}

function isShoppingListAdmin(shoppingList) {
  const _userId = store.getters['auth/userName'];

  if (shoppingList.AdminUser === _userId) {
    return true;
  }
  return false;
}

/**
 * Add a new shopping list to current list array in store
 * @param {object} list The list needs to be added
 */
function addShoppingList(list) {
  if (list == null || Object.keys(list).length === 0) {
    return;
  }
  const allLists = store.getters['shoppingList/orders'];
  const newLists = [...allLists, ...[list]];
  store.commit('shoppingList/setOrders', newLists);
}

/**
 * Update an existing shopping list in store using the new one
 * @param {object} list The new shopping list
 */
function updateShoppingList(list) {
  if (list == null || Object.keys(list).length === 0) {
    return;
  }
  const allLists = store.getters['shoppingList/orders'];
  const newLists = [...allLists];
  const currListIndex = newLists.findIndex(
    (item) => item.ShoppingListId === list.ShoppingListId
  );
  if (currListIndex >= 0) {
    newLists[currListIndex] = { ...list };
  }
  store.commit('shoppingList/setOrders', newLists);
}

/**
 * Load all shopping lists for user and save them in store/shoppingList
 * @returns Promise
 */
function getShoppingLists() {
  const _userLoggedIn = store.getters['auth/isValid'];

  if (!_userLoggedIn) {
    return Promise.reject(new Error('User is not logged in'));
  }

  const _userId = store.getters['auth/userName'];

  return new Promise((resolve, reject) => {
    doRequest(
      (response) => {
        const lists = response.data.ShoppingLists;
        store.commit('shoppingList/setOrders', lists);

        if (lists.length <= 0) {
          store.commit('setCurrentSelectedShoppingList', {});
        }

        resolve(response);
      },
      reject,
      'getShoppingLists',
      _userId,
      {}
    );
  });
}

function getAwardedGrowthVitaminCards(shoppingListId) {
  const payload = {
    Reservations: {},
    ShoppingListId: shoppingListId,
  };

  const _userId = store.getters['auth/userName'];

  return new Promise((resolve, reject) => {
    doRequest(
      resolve,
      reject,
      'getAwardedGrowthVitaminCards',
      _userId,
      payload
    );
  });
}

/**
 * Add product item to an order
 * @param {object} payload Properties in payload:
 *                         - CreateShoppingList
 *                           (optional, used for adding ptoduct to an empty shopping list)
 *                         - ShoppingListName
 *                           (optional, used for adding ptoduct to an empty shopping list)
 *                         - ShoppingListId
 *                         - OrderId
 *                         - EventId
 *                         - ProductId
 *                         - ItemId
 *                         - Quantity
 *                         - OptionSelection
 * @returns Promise
 */
function addProductToReservation(payload) {
  const _userId = store.getters['auth/userName'];

  return new Promise((resolve, reject) => {
    doRequest(
      (response) => {
        if (response.data.ShoppingList != null) {
          store.commit(
            'setCurrentSelectedShoppingList',
            response.data.ShoppingList
          );

          if (payload.CreateShoppingList) {
            // Add the created list to store
            addShoppingList(response.data.ShoppingList);
          } else {
            // Update existing list in store
            updateShoppingList(response.data.ShoppingList);
          }
        } else {
          getShoppingLists();
        }

        window.EventBus.fire('shoppinglistDialog', 'added');

        resolve(response);
      },
      (error) => {
        _onAddProductToReservationError(error);
        reject(error);
      },
      'addProductToReservation',
      _userId,
      payload
    );
  });
}

/**
 * Change product item and / or quantity for an order
 * @param {object} payload Properties in payload:
 *                         - ShoppingListId
 *                         - OrderId
 *                         - EventId
 *                         - ProductId
 *                         - ItemId
 *                         - Quantity
 *                         - NewItemId (optional, used for chaning to another item)
 *                         - OrderItemId
 * @returns Promise
 */
function changeProductOnReservation(payload) {
  const _userId = store.getters['auth/userName'];

  return new Promise((resolve, reject) => {
    doRequest(
      (response) => {
        if (response.data.ShoppingList != null) {
          store.commit(
            'setCurrentSelectedShoppingList',
            response.data.ShoppingList
          );

          // Update existing list in store
          updateShoppingList(response.data.ShoppingList);
        } else {
          getShoppingLists();
        }

        window.EventBus.fire('activateSnackBar', {
          text: translate('ITEM_CHANGE_SAVED'),
          color: 'success',
        });

        resolve(response);
      },
      (error) => {
        _onChangeProductOnReservationError(error);
        reject(error);
      },
      'changeProductOnReservation',
      _userId,
      payload
    );
  });
}

/**
 * Remove product item from an order
 * @param {object} payload Properties in payload:
 *                         - ShoppingListId
 *                         - OrderId
 *                         - EventId
 *                         - ProductId
 *                         - ItemId
 *                         - OrderItemId
 * @returns Promise
 */
function removeProductFromReservation(payload) {
  const _userId = store.getters['auth/userName'];

  return new Promise((resolve, reject) => {
    doRequest(
      (response) => {
        window.EventBus.fire('activateSnackBar', {
          text: translate('ITEM_REMOVED'),
          color: 'success',
        });

        resolve(response);
      },
      reject,
      'removeProductFromReservation',
      _userId,
      payload
    );
  });
}

/**
 * Update repeating shopping list with current order
 * @param {object} payload Properties in payload:
 *                         - ShoppingListId
 * @returns Promise
 */
function updateShoppingListProductsToCurrentOrder(payload) {
  const _userId = store.getters['auth/userName'];

  return new Promise((resolve, reject) => {
    doRequest(
      (response) => {
        window.EventBus.fire('activateSnackBar', {
          text: translate('UPDATE_SUBSCRIPTION_SUCCESS'),
          color: 'success',
        });

        resolve(response);
      },
      (error) => {
        window.EventBus.fire('activateSnackBar', {
          text: translate('UPDATE_SUBSCRIPTION_ERROR'),
          color: 'error',
        });

        reject(error);
      },
      'updateShoppingListProductsToCurrentOrder',
      _userId,
      payload
    );
  });
}

/**
 * Reset repeating shopping list to the original one
 * @param {object} payload Properties in payload:
 *                         - ShoppingListId
 * @returns Promise
 */
function resetOrderToShoppingList(payload) {
  const _userId = store.getters['auth/userName'];

  return new Promise((resolve, reject) => {
    doRequest(
      (response) => {
        window.EventBus.fire('activateSnackBar', {
          text: translate('RESET_SUBSCRIPTION_SUCCESS'),
          color: 'success',
        });

        resolve(response);
      },
      (error) => {
        window.EventBus.fire('activateSnackBar', {
          text: translate('RESET_SUBSCRIPTION_ERROR'),
          color: 'error',
        });

        reject(error);
      },
      'resetOrderToShoppingList',
      _userId,
      payload
    );
  });
}

export {
  getShoppingLists,
  getAwardedGrowthVitaminCards,
  isShoppingListAdmin,
  addProductToReservation,
  changeProductOnReservation,
  removeProductFromReservation,
  updateShoppingListProductsToCurrentOrder,
  resetOrderToShoppingList,
};
